import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import mixpanel from 'mixpanel-browser'

// Import global styles
import './styles/global.css'
import { queryClient } from './modules/queryClient'

// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { initSentry } from '@/instrument.sentry'
import { isClientDevEnv } from '@/client-env'

initSentry()

const reactStrictMode = isClientDevEnv

// 🏴‍☠️ Expose deployment tag on window for debugging and monitoring
declare global {
  interface Window {
    __deploy_tag?: string
  }
}

// Set deployment tag on window if available
if (import.meta.env.VITE_DEPLOYMENT_TAG) {
  window.__deploy_tag = import.meta.env.VITE_DEPLOYMENT_TAG
}

// Create a new router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
  scrollRestorationBehavior: 'instant',
  scrollToTopSelectors: ['#main-content'],
  defaultViewTransition: true,
})

const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN

if (!mixpanelToken && import.meta.env.MODE !== 'development') {
  throw new Error('VITE_MIXPANEL_TOKEN is required for non-development builds')
}

if (mixpanelToken && !isClientDevEnv) {
  mixpanel.init(mixpanelToken, {
    debug: false,
    track_pageview: false,
    persistence: 'localStorage',
    record_sessions_percent: 30, // Record 30% of non-logged-in users (manual trigger on log)
    record_heatmap_data: true,
    record_mask_all_text: false,
    record_mask_text_selector: ['#billing-section'],
    api_host: '/_mp',
    cdn: '/_mp',
  })

  // Track pageviews on every SPA navigation
  router.subscribe('onResolved', () => {
    mixpanel.track_pageview({ page: window.location.pathname })
  })
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  const app = (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )

  root.render(
    reactStrictMode ? (
      <React.StrictMode>{app}</React.StrictMode>
    ) : (
      app
    )
  )
}
